import React from 'react';
import aiStudentsImage from '../../../asset/images/home/ai_students.png';

export const StudentsImage = ({ studentsImage, showConfusion, shouldStopAnimations }) => {
  const imageSrc = studentsImage || aiStudentsImage;
  
  return (
    <div className="relative w-full flex justify-center mb-4">
      <img
        src={imageSrc}
        alt="AI Students"
        className={`w-full h-auto max-h-64 object-cover rounded-lg shadow-sm transition-opacity duration-300 ${
          showConfusion && !shouldStopAnimations ? 'animate-pulse' : ''
        }`}
      />
      {showConfusion && !shouldStopAnimations && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-4xl animate-bounce">😕</div>
        </div>
      )}
    </div>
  );
};

export const RecordingTimer = ({ recordingTime, maxTime = 10, isRecording }) => {
  if (!isRecording) return null;
  
  const timeLeft = maxTime - recordingTime;
  const percentage = (timeLeft / maxTime) * 100;
  
  return (
    <div 
      className="fixed top-20 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 font-bold text-lg"
      style={{ 
        minWidth: '120px',
        textAlign: 'center'
      }}
    >
      {timeLeft}s
    </div>
  );
};

export const MicButton = ({ 
  isRecording, 
  isProcessing, 
  isPlayingReminder,
  onStartRecording, 
  onStopRecording,
  disabled = false 
}) => {
  const handleClick = () => {
    if (isRecording) {
      onStopRecording();
    } else {
      onStartRecording();
    }
  };
  
  const isDisabled = disabled || isProcessing || isPlayingReminder;
  
  return (
    <div className="flex justify-center mt-4">
      <button
        onClick={handleClick}
        disabled={isDisabled}
        className={`
          w-20 h-20 rounded-full flex items-center justify-center
          transition-all duration-200 shadow-lg
          ${isRecording 
            ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
            : isDisabled
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700'
          }
          text-white
        `}
        title={isRecording ? 'Stop Recording' : 'Start Recording'}
      >
        {isProcessing ? (
          <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg 
            className="w-10 h-10" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4z" />
            <path d="M5.5 9.643a.75.75 0 00-1.5 0V10c0 3.06 2.29 5.585 5.25 5.954V17.5h-1.5a.75.75 0 000 1.5h4.5a.75.75 0 000-1.5h-1.5v-1.546A6.001 6.001 0 0016 10v-.357a.75.75 0 00-1.5 0V10a4.5 4.5 0 01-9 0v-.357z" />
          </svg>
        )}
      </button>
    </div>
  );
};

export const FlashcardGrid = ({ 
  flashcards, 
  currentFlashcardIndex, 
  selectedFlashcard,
  flashcardResult,
  onFlashcardClick,
  canClickFlashcard,
  showReminderIcon,
  onReminderClick
}) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
      {flashcards.map((flashcard, index) => {
        const isSelected = selectedFlashcard?.id === flashcard.id;
        const isCurrent = index === currentFlashcardIndex;
        const result = flashcardResult[flashcard.id];
        const canClick = canClickFlashcard(index);
        
        return (
          <div
            key={flashcard.id}
            onClick={() => canClick && onFlashcardClick(flashcard, index)}
            className={`
              relative p-4 rounded-lg border-2 cursor-pointer transition-all
              ${isSelected 
                ? 'border-blue-600 bg-blue-50 scale-105' 
                : isCurrent
                ? 'border-yellow-400 bg-yellow-50'
                : canClick
                ? 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
                : 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
              }
              ${result === 'correct' ? 'border-green-500 bg-green-50' : ''}
              ${result === 'incorrect' ? 'border-red-500 bg-red-50' : ''}
            `}
          >
            <div className="text-center">
              <div className="text-2xl font-bold mb-2">{flashcard.word}</div>
              {showReminderIcon && flashcard.reminderAudio && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReminderClick(flashcard);
                  }}
                  className="absolute top-2 left-2 p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                  title="Play reminder audio"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20" strokeWidth="1.5">
                    <path d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.793L4.383 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.383l4-3.617a1 1 0 011.617.793zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

